/**
 * Migrate assessments from localStorage to database
 * This can be called from browser console or a migration page
 */

export async function migrateLocalStorageToDatabase() {
  console.log('🔄 Starting migration from localStorage to database...');
  
  try {
    // Get all assessments from localStorage
    const stored = localStorage.getItem('health_assessments');
    if (!stored) {
      console.log('❌ No assessments found in localStorage');
      return { success: false, message: 'No assessments in localStorage' };
    }

    const allAssessments = JSON.parse(stored);
    const assessmentArray = Object.values(allAssessments);
    
    console.log(`📦 Found ${assessmentArray.length} assessments in localStorage`);

    let migrated = 0;
    let failed = 0;
    const errors = [];

    // Migrate each assessment
    for (const assessment of assessmentArray) {
      try {
        console.log(`\n📝 Migrating assessment ${assessment.id}...`);
        console.log(`   User: ${assessment.personalInfo?.email}`);
        console.log(`   Status: ${assessment.status}`);

        // First, ensure user exists in database
        const userEmail = assessment.personalInfo?.email;
        if (!userEmail) {
          console.log('   ⚠️  No email found, skipping');
          failed++;
          errors.push({ id: assessment.id, error: 'No email' });
          continue;
        }

        // Create/update user in database
        const userResponse = await fetch('/api', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'user.create',
            data: {
              email: userEmail,
              display_name: `${assessment.personalInfo?.firstName || ''} ${assessment.personalInfo?.lastName || ''}`.trim(),
              first_name: assessment.personalInfo?.firstName,
              last_name: assessment.personalInfo?.lastName,
              sport: assessment.personalInfo?.sport,
              school_name: assessment.personalInfo?.schoolName,
              grade: assessment.personalInfo?.grade,
              role: 'user',
              provider: 'email'
            }
          })
        });

        if (!userResponse.ok) {
          console.log('   ⚠️  User creation failed, but continuing...');
        } else {
          console.log('   ✅ User synced');
        }

        // Create assessment in database
        const assessmentResponse = await fetch('/api', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'assessment.create',
            data: {
              email: userEmail,
              personalInfo: assessment.personalInfo,
              status: assessment.status || 'in_progress'
            }
          })
        });

        if (!assessmentResponse.ok) {
          const errorText = await assessmentResponse.text();
          console.log(`   ❌ Assessment creation failed: ${errorText}`);
          failed++;
          errors.push({ id: assessment.id, error: errorText });
          continue;
        }

        const { assessment: createdAssessment } = await assessmentResponse.json();
        console.log(`   ✅ Assessment created with ID: ${createdAssessment.id}`);

        // Update assessment with all data
        const updateData = {
          id: createdAssessment.id,
          updates: {}
        };

        if (assessment.nutritionResponses) {
          updateData.updates.nutrition_responses = assessment.nutritionResponses;
        }
        if (assessment.cognitiveResponses) {
          updateData.updates.cognitive_responses = assessment.cognitiveResponses;
        }
        if (assessment.physicalTests) {
          updateData.updates.physical_tests = assessment.physicalTests;
        }
        if (assessment.status) {
          updateData.updates.status = assessment.status;
        }

        const updateResponse = await fetch('/api', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'assessment.update',
            data: updateData
          })
        });

        if (!updateResponse.ok) {
          console.log('   ⚠️  Assessment update failed, but base assessment created');
        } else {
          console.log('   ✅ Assessment data updated');
        }

        migrated++;
        console.log(`   ✅ Migration complete for ${assessment.id}`);

      } catch (error) {
        console.error(`   ❌ Error migrating assessment ${assessment.id}:`, error);
        failed++;
        errors.push({ id: assessment.id, error: error.message });
      }
    }

    console.log('\n' + '='.repeat(50));
    console.log('📊 Migration Summary:');
    console.log(`   ✅ Migrated: ${migrated}`);
    console.log(`   ❌ Failed: ${failed}`);
    console.log(`   📦 Total: ${assessmentArray.length}`);
    
    if (errors.length > 0) {
      console.log('\n❌ Errors:');
      errors.forEach(err => {
        console.log(`   - ${err.id}: ${err.error}`);
      });
    }

    return {
      success: true,
      migrated,
      failed,
      total: assessmentArray.length,
      errors
    };

  } catch (error) {
    console.error('❌ Migration failed:', error);
    return {
      success: false,
      message: error.message
    };
  }
}

// Make it available globally for console access
if (typeof window !== 'undefined') {
  window.migrateLocalStorageToDatabase = migrateLocalStorageToDatabase;
}
